'use client';
import MuiAvatar from '@mui/material/Avatar';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { neutral4 } from '../../colors';
import idsTokensDefault from '../../utils/tokens/idsTokens';
import withRef from '../../utils/withRef';

export const styles = ({ idsTokens = idsTokensDefault }, { stateless = false }) => ({
  root: {
    borderRadius: idsTokens.avatar.default.radius,
    border: '2px solid',
    borderColor: idsTokens.avatar.default.color.border.enabled,
    '&:focus-visible': {
      borderColor: idsTokens.avatar.default.color.border.focus,
      outlineOffset: '2px',
      outline: stateless
        ? 'none'
        : `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.functional.color.border.focus}`,
    },
    '&:hover': {
      backgroundBlendMode: 'overlay',
      borderColor: stateless
        ? idsTokens.avatar.default.color.border.enabled
        : idsTokens.avatar.default.color.border.hover,
    },
    '&:active': {
      borderColor: stateless ? 'none' : idsTokens.avatar.default.color.border.pressed,
      outline: '2px solid transparent',
    },
  },
  extraSmall: {
    height: idsTokens.avatar.default.sizing.xs,
    width: idsTokens.avatar.default.sizing.xs,
    fontSize: idsTokens.typography.font.size.xs,
    '& svg': {
      fontSize: 16,
    },
    '& img': {
      height: idsTokens.avatar.default.sizing.xs,
      width: idsTokens.avatar.default.sizing.xs,
      background: 'lightgray 50%',
    },
  },
  small: {
    height: idsTokens.avatar.default.sizing.small,
    width: idsTokens.avatar.default.sizing.small,
    fontSize: idsTokens.typography.font.size.sm,
    '& svg': {
      fontSize: 20,
    },
    '& img': {
      height: idsTokens.avatar.default.sizing.small,
      width: idsTokens.avatar.default.sizing.small,
      background: 'lightgray 50%',
    },
  },
  medium: {
    height: idsTokens.avatar.default.sizing.medium,
    width: idsTokens.avatar.default.sizing.medium,
    fontSize: idsTokens.typography.font.size.xl,
    '& svg': {
      fontSize: 24,
    },
    '& img': {
      height: idsTokens.avatar.default.sizing.medium,
      width: idsTokens.avatar.default.sizing.medium,
    },
  },
  large: {
    height: idsTokens.avatar.default.sizing.large,
    width: idsTokens.avatar.default.sizing.large,
    fontSize: idsTokens.typography.font.size['2x'],
    '& svg': {
      fontSize: 32,
    },
    '& img': {
      height: idsTokens.avatar.default.sizing.large,
      width: idsTokens.avatar.default.sizing.large,
    },
  },
  extraLarge: {
    height: idsTokens.avatar.default.sizing.xl,
    width: idsTokens.avatar.default.sizing.xl,
    fontSize: idsTokens.typography.font.size['7x'],
    '& svg': {
      fontSize: 72,
    },
    '& img': {
      height: idsTokens.avatar.default.sizing.xl,
      width: idsTokens.avatar.default.sizing.xl,
    },
  },
  dark: {
    backgroundColor: neutral4,
  },
  light: {
    background: idsTokens.avatar.default.color.background,
  },
  stateless: {
    '&.MuiAvatar-root:hover': {
      outline: 'none',
      backgroundColor: idsTokens.avatar.default.color.background,
      backgroundBlendMode: 'none',
    },
    '&.MuiAvatar-root:active': {
      outline: 'none',
    },
  },
});

const useStyles = makeStyles()(styles);

export const Avatar = ({
  className,
  size = 'medium',
  color = 'light',
  stateless = false,
  forwardedRef: ref,
  ...rest
}) => {
  const { classes, cx } = useStyles({ size, stateless });

  return (
    <MuiAvatar
      className={cx(
        classes.root,
        classes[size],
        classes[color],
        stateless && classes.stateless,
        className
      )}
      {...rest}
      ref={ref}
    />
  );
};

Avatar.propTypes = {
  /**
   * Used in combination with `src` or `srcSet` to provide alternative text for
   * the rendered `img` element.
   */
  alt: PropTypes.string,
  /**
   * Used to render icon or text elements inside the `Avatar` if `src` is not set.
   * This can be an element, or just a string.
   */
  children: PropTypes.node,
  /**
   * The color of the component.
   *
   * @deprecated Only one default color is supported now.
   */
  color: PropTypes.oneOf(['dark', 'light']),
  /**
   * Attributes applied to the `img` element if the component is used to display
   * an image. It can be used to listen for the loading error event.
   */
  imgProps: PropTypes.object,
  /** The size of the component. */
  size: PropTypes.oneOf(['extraSmall', 'small', 'medium', 'large', 'extraLarge']),
  /** The `src` attribute for the `img` element. */
  src: PropTypes.string,
  /** If `true`, the component renders without hover and pressed states. */
  stateless: PropTypes.bool,
};

export default withRef()(Avatar);
